import * as _ from 'lodash';
import * as angular from 'angular';
import * as d3 from 'd3';
import * as moment from 'moment';

import { AlarmLevel, DateValue, IFrequency, IGroupSchedulingViewModelRow, KeyValuePair, SparkLineData, SparkLineDataDictionary } from '../../models';
import { IFrequencyFormatHelperService, IsparklineHelperService } from '../../services/helpers';
import { ICurrentApplicationService } from '../../services';
import { IOnlineComputationService } from './onlineComputation.service';

export interface ISparklineService {
    getSparklines(displayedGroups: IGroupSchedulingViewModelRow[], sparkLineWidth: number, sparkLineHeight: number): angular.IPromise<SparkLineDataDictionary>;
}

export class SparklineService implements ISparklineService {
    static serviceId = 'sparklineService';

    static $inject = [
        '$q',
        'onlineComputationService',
        'sparklineHelperService',
        'currentApplicationService',
        'frequencyFormatHelperService',
    ];

    constructor(
        private _$q: angular.IQService,
        private _onlineComputationService: IOnlineComputationService,
        private _sparklineHelper: IsparklineHelperService,
        private _currentApplicationService: ICurrentApplicationService,
        private _frequencyFormatHelperService: IFrequencyFormatHelperService,
    ) { }

    getSparklines(displayedGroups: IGroupSchedulingViewModelRow[], sparkLineWidth: number, sparkLineHeight: number): angular.IPromise<SparkLineDataDictionary> {
        const promises = [
            this._onlineComputationService.getElapsedPrevious(),
            this._onlineComputationService.getElapsedAcquisition()
        ];
        return this._$q.all(promises)
            .then(responses => {
                const elapsedPrevious = responses[0] as _.Dictionary<_.Dictionary<number>>;
                const elapsedAcquisition = responses[1] as KeyValuePair<string, number>[];
                const numberOfScheduledGroups = displayedGroups.length;
                const dictionary: SparkLineDataDictionary = {};
                displayedGroups
                    .filter(g => g.isScheduled)
                    .forEach(group => {
                        const sparkLineData: SparkLineData = this.getSparklineData(elapsedPrevious, group, elapsedAcquisition, numberOfScheduledGroups, sparkLineWidth, sparkLineHeight);
                        dictionary[group.guid] = sparkLineData;
                    });
                return dictionary;
            });
    }

    private getSparklineData(
        elapsedPrevious: _.Dictionary<_.Dictionary<number>>,
        group: IGroupSchedulingViewModelRow,
        elapsedAcquisition: KeyValuePair<string, number>[],
        numberOfScheduledGroups: number,
        sparkLineWidth: number,
        sparkLineHeight: number
    ) {
        const sparkLineData: SparkLineData = {
            domain: [null, null],
            yDomain: [0, 0],
            path: '',
            average: ''
        };
        let data: DateValue[] = elapsedPrevious[group.guid]
            ? Object.keys(elapsedPrevious[group.guid]).map(k => {
                return { timeStamp: k, value: elapsedPrevious[group.guid][k] };
            })
            : [];
        data = _.sortBy(data, d => d.timeStamp);
        const dataAcquisition: DateValue[] = elapsedAcquisition
            .map(ep => { return { timeStamp: ep.key, value: ep.value }; });
        const numbers = d3.extent(data, (e: any) => e.timeStamp);
        const values = data.map(d => d.value);
        const threshold = this.getBaseFreqInSeconds();
        const min = 0;
        const max = d3.max([values.length ? d3.max(values) : 0, threshold]);
        let avg = values.length ? d3.mean(values) : 0;
        const avgAcquisition = dataAcquisition.length ? d3.mean(dataAcquisition, (e: any) => e.value) : 0;
        if (numberOfScheduledGroups === 1) {
            avg = avg + avgAcquisition;
        }
        let formatTime = 'mm:ss';
        if (avg > 3600) {
            formatTime = 'HH:mm:ss';
        }
        let formatTimeAcquisition = 'mm:ss';
        if (avgAcquisition > 3600) {
            formatTimeAcquisition = 'HH:mm:ss';
        }
        const average = moment.utc(moment.duration(avg, 's').asMilliseconds()).format(formatTime);
        const averageAcquisition = moment.utc(moment.duration(avgAcquisition, 's').asMilliseconds()).format(formatTimeAcquisition);
        sparkLineData.average = average;
        if (numberOfScheduledGroups > 1) {
            sparkLineData.averageAcquisition = averageAcquisition;
        }
        this._sparklineHelper
            .resize(sparkLineWidth, sparkLineHeight)
            .data(data)
            .yDomain([min, max])
            .xDomain([moment(numbers[0]).toDate(), moment(numbers[1]).toDate()]);
        sparkLineData.path = this._sparklineHelper.path(data, true);
        sparkLineData.domain = this._sparklineHelper.xDomain();
        sparkLineData.yDomain = this._sparklineHelper.yDomain();
        sparkLineData.ys = [this._sparklineHelper.scaleY(threshold)];
        sparkLineData.level = AlarmLevel.Critical;
        sparkLineData.ranges = this.getRanges(data, threshold);
        return sparkLineData;
    }

    private getBaseFreqInSeconds(): number {
        const cfg = this._currentApplicationService.getApplicationConfiguration();
        const baseFreq: IFrequency = { interval: cfg.interval, intervalTimeUnit: cfg.intervalTimeUnit };
        return this._frequencyFormatHelperService.toDuration(baseFreq).asSeconds();
    }

    private getRanges(data: DateValue[], threshold: number): { start: string; end: string; level: AlarmLevel }[] {
        const ranges = [];
        // index by order and filter according to threshold
        const valuesInAlarm = _.sortBy(data, d => new Date(d.timeStamp))
            .map((d, i) => { return { index: i, data: d }; })
            .filter(o => o.data.value > threshold);

        if (valuesInAlarm.length > 0) {
            // starting with first datevalue
            let prev = valuesInAlarm[0].index - 1;
            let currentRange = this.getRange(valuesInAlarm[0].data);
            valuesInAlarm.forEach(v => {
                if (v.index === prev + 1) {
                    // if next date, just update the end of range 
                    currentRange.end = v.data.timeStamp;
                } else {
                    // we jumped a date close the range and start a new one
                    ranges.push(currentRange);
                    currentRange = this.getRange(v.data);
                }
                prev = v.index;
            });
            // add the last date closing it's range
            //currentRange.end = endDate;
            ranges.push(currentRange);
        }

        return ranges;
    }

    private getRange(current: DateValue) {
        return { start: current.timeStamp, end: current.timeStamp, level: AlarmLevel.Critical };
    }
}